import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useColorModeValue,
  Badge,
  IconButton,
  Image,
  Flex,
  Spacer
} from '@chakra-ui/react';
import { FiX, FiExternalLink, FiPlay, FiAward } from 'react-icons/fi';
import IconWrapper from './IconWrapper';
import useAdFree from '../hooks/useAdFree';

interface AdBannerProps {
  type: 'pre-roll' | 'mid-roll' | 'banner';
  onClose?: () => void;
  onSkip?: () => void;
  autoClose?: boolean;
  duration?: number;
}

const AdBanner: React.FC<AdBannerProps> = ({
  type,
  onClose,
  onSkip,
  autoClose = false,
  duration = 5
}) => {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [isVisible, setIsVisible] = useState(true);
  const { isAdFree, isLoading } = useAdFree();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const adTextColor = useColorModeValue('gray.600', 'gray.300');

  // Mock ad data
  const adData = {
    title: 'Support Independent Creators',
    description: 'Discover amazing content from talented independent creators on Vindie Stream',
    image: undefined,
    cta: 'Explore Now',
    sponsor: 'Vindie Stream'
  };

  useEffect(() => {
    if (autoClose && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (autoClose && timeLeft === 0) {
      onSkip?.();
    }
  }, [timeLeft, autoClose, onSkip]);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const handleSkip = () => {
    onSkip?.();
  };

  const handleClick = () => {
    // Simulate ad click
    window.open('https://vindiestream.com', '_blank');
  };

  // Don't show ads if user has ad-free subscription
  if (isAdFree) {
    return (
      <Box
        bg="green.50"
        border="1px"
        borderColor="green.200"
        borderRadius="lg"
        p={3}
        mb={4}
        textAlign="center"
      >
        <HStack justify="center" gap={2}>
          <IconWrapper icon={FiAward} size={16} color="green.500" />
          <Text fontSize="sm" color="green.700" fontWeight="medium">
            Ad-free viewing active - No ads for you!
          </Text>
        </HStack>
      </Box>
    );
  }

  if (!isVisible) return null;

  if (type === 'pre-roll' || type === 'mid-roll') {
    return (
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="black"
        zIndex={100}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack gap={4} maxW="600px" mx="auto" p={6}>
          <Box position="relative" w="100%" maxW="500px">
            <Image
              src={adData.image}
              alt="Advertisement"
              w="100%"
              h="300px"
              objectFit="cover"
              borderRadius="lg"
            />
            
            {/* Skip Button */}
            {timeLeft > 0 ? (
              <Badge
                position="absolute"
                top={4}
                right={4}
                bg="blackAlpha.700"
                color="white"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="sm"
              >
                Skip in {timeLeft}s
              </Badge>
            ) : (
              <Button
                position="absolute"
                top={4}
                right={4}
                size="sm"
                colorScheme="red"
                onClick={handleSkip}
              >
                Skip Ad
              </Button>
            )}
          </Box>

          <VStack gap={2} textAlign="center">
            <Text fontSize="lg" fontWeight="bold" color="white">
              {adData.title}
            </Text>
            <Text fontSize="sm" color="gray.300">
              {adData.description}
            </Text>
            <Text fontSize="xs" color="gray.400">
              Sponsored by {adData.sponsor}
            </Text>
          </VStack>

          <Button
            colorScheme="purple"
            size="lg"
            leftIcon={<IconWrapper icon={FiPlay} size={20} />}
            onClick={handleClick}
          >
            {adData.cta}
          </Button>
        </VStack>
      </Box>
    );
  }

  // Banner ad
  return (
    <Box
      bg={bgColor}
      border="1px"
      borderColor={borderColor}
      borderRadius="lg"
      p={4}
      mb={4}
      position="relative"
    >
      <Flex align="start" gap={4}>
        <Image
          src={adData.image}
          alt="Advertisement"
          w="120px"
          h="80px"
          objectFit="cover"
          borderRadius="md"
        />
        
        <VStack align="start" gap={2} flex="1">
          <HStack>
            <Badge colorScheme="purple" variant="subtle" size="sm">
              Ad
            </Badge>
            <Text fontSize="sm" fontWeight="bold" color={textColor}>
              {adData.title}
            </Text>
          </HStack>
          
          <Text fontSize="sm" color={adTextColor} noOfLines={2}>
            {adData.description}
          </Text>
          
          <HStack gap={2}>
            <Button
              size="sm"
              colorScheme="purple"
              variant="outline"
              leftIcon={<IconWrapper icon={FiExternalLink} size={14} />}
              onClick={handleClick}
            >
              {adData.cta}
            </Button>
            <Text fontSize="xs" color="gray.500">
              by {adData.sponsor}
            </Text>
          </HStack>
        </VStack>
        
        <IconButton
          aria-label="Close ad"
          icon={<IconWrapper icon={FiX} size={16} />}
          size="sm"
          variant="ghost"
          onClick={handleClose}
        />
      </Flex>
    </Box>
  );
};

export default AdBanner;
